/**
 * <region-restriction>
 * Handles the product regional-availability modal. The list of allowed
 * countries (the countries we sell to, minus the inventory.Limitations
 * blocklist) is rendered server-side in Liquid, each <li> carrying
 * data-iso and data-name. This element only:
 *  - opens/closes the "Check region restrictions" modal
 *  - filters the country list with the search input
 */
if (!customElements.get('region-restriction')) {
  class RegionRestriction extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.region-modal');
      this.openBtn = this.querySelector('[data-region-open]');
      this.list = this.querySelector('[data-region-list]');
      this.searchInput = this.querySelector('[data-region-search]');
      this.emptyMsg = this.querySelector('[data-region-empty]');
      this.isOpen = false;

      // Portal the modal to <body> so it escapes any ancestor stacking
      // context (transforms, z-index, overflow) and renders above the header.
      if (this.modal) {
        document.body.appendChild(this.modal);
      }

      if (this.openBtn) {
        this.openBtn.addEventListener('click', () => this.open());
      }
      if (this.modal) {
        this.modal.querySelectorAll('[data-region-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );
      }
      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.filter());
      }
      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      };
      document.addEventListener('keydown', this._onKeydown);

      this._initGeoipCountry();
    }

    // The row rendered by Liquid reflects localization.country, i.e. the
    // market/country the shopper has selected (or Shopify's own
    // first-visit guess) — not necessarily their real location. CSS hides
    // that row and shows the loading row instead as soon as html.js is
    // present (see component-main-product-info.css), so we never flash
    // the market-based guess before correcting it. Once we resolve (or
    // give up on) the IP lookup, adding .is-resolved flips CSS back to
    // showing the real row — already holding the right values if IP
    // detection succeeded, or left exactly as Liquid rendered it (i.e.
    // the market/localization fallback) if it didn't.
    _initGeoipCountry() {
      if (!window.geoip2 || typeof window.geoip2.country !== 'function') {
        this.classList.add('is-resolved');
        return;
      }

      const limitations = (this.dataset.limitations || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(fallbackTimer);
        this.classList.add('is-resolved');
      };
      const fallbackTimer = setTimeout(finish, 5000);

      window.geoip2.country((info) => {
        if (settled) return;
        const iso = info && info.country && info.country.iso_code;
        const name = info && info.country_name;
        if (iso && name) {
          // limitations lists the countries this product CANNOT be
          // activated in (sanctioned/excluded regions).
          this._renderRegionStatus(!limitations.includes(iso), name);
        }
        finish();
      }, finish);
    }

    _renderRegionStatus(isAllowed, countryName) {
      const icon = this.querySelector('[data-region-icon]');
      if (icon) {
        const template = this.querySelector(
          `[data-region-icon-template="row-${isAllowed ? 'allowed' : 'blocked'}"]`
        );
        if (template) icon.innerHTML = template.innerHTML;
        icon.classList.toggle('is-allowed', isAllowed);
        icon.classList.toggle('is-blocked', !isAllowed);
      }

      const status = this.querySelector('[data-region-status]');
      if (status) {
        const prefix = isAllowed ? status.dataset.allowedText : status.dataset.blockedText;
        status.textContent = `${prefix}${countryName}`;
        status.classList.toggle('is-allowed', isAllowed);
        status.classList.toggle('is-blocked', !isAllowed);
      }

      if (!this.modal) return;

      const modalIcon = this.modal.querySelector('[data-region-modal-icon]');
      if (modalIcon) {
        const template = this.querySelector(
          `[data-region-icon-template="modal-${isAllowed ? 'allowed' : 'blocked'}"]`
        );
        if (template) modalIcon.innerHTML = template.innerHTML;
        modalIcon.classList.toggle('is-allowed', isAllowed);
        modalIcon.classList.toggle('is-blocked', !isAllowed);
      }

      const modalVerb = this.modal.querySelector('[data-region-modal-verb]');
      if (modalVerb) modalVerb.textContent = isAllowed ? ' can' : ' cannot';

      const modalCountry = this.modal.querySelector('[data-region-modal-country]');
      if (modalCountry) modalCountry.textContent = countryName;

      const modalCountryMeta = this.modal.querySelector('[data-region-modal-country-meta]');
      if (modalCountryMeta) modalCountryMeta.textContent = countryName.toUpperCase();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      // Remove the portaled modal so it doesn't outlive its element
      // (e.g. when a section is re-rendered in the theme editor).
      if (this.modal && this.modal.parentNode) {
        this.modal.parentNode.removeChild(this.modal);
      }
      document.body.classList.remove('region-modal-open');
    }

    filter() {
      if (!this.list) return;
      const q = this.searchInput.value.trim().toLowerCase();
      let visible = 0;
      this.list.querySelectorAll('.region-modal__country').forEach((item) => {
        const match =
          !q ||
          (item.dataset.name || '').includes(q) ||
          (item.dataset.iso || '').toLowerCase().includes(q);
        item.hidden = !match;
        if (match) visible += 1;
      });
      if (this.emptyMsg) this.emptyMsg.hidden = visible !== 0;
    }

    open() {
      if (!this.modal) return;
      this.modal.hidden = false;
      document.body.classList.add('region-modal-open');
      this.isOpen = true;
      const dialog = this.modal.querySelector('.region-modal__dialog');
      if (dialog) dialog.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('region-modal-open');
      this.isOpen = false;
      if (this.openBtn) this.openBtn.focus();
    }
  }

  customElements.define('region-restriction', RegionRestriction);
}
